import { useRouter } from 'next/router'
import { Helmet } from 'react-helmet'

import { DEFAULT_PAGE_DESCRIPTION, DEFAULT_PAGE_IMAGE, DEFAULT_PAGE_TITLE } from '@web/libs/constants'
import links from '@web/libs/constants/links'
import translate from '@web/libs/translate/translate'
import { Nullish } from '@web/types'

const toHttps = (url: string) => {
  const httpToken = 'http://'
  const httpsToken = 'https://'
  if (url.startsWith(httpToken)) return url.replace(httpToken, httpsToken)
  return url
}

export interface MetaTagsProp {
  title?: Nullish<string>
  description?: Nullish<string>
  author?: Nullish<string>
  image?: {
    url: string
    width?: number
    height?: number
  }
  pageUrl?: string
  videoType?: string
  videoId?: string
  channelName?: string
  duration?: string
}

/**
 * og tag 디버깅 툴
 * kakao talk https://developers.kakao.com/tool/debugger/sharing
 * band https://developers.band.us/developers/ko/docs/share/debugger
 * facebook https://developers.facebook.com/tools/debug/?locale=ko_KR
 *
 * 이미지 비율
 * https://blog.naver.com/parosaone/222421433222
 */
const MetaTags = (props: MetaTagsProp) => {
  const router = useRouter()
  const { t } = translate({ contextType: 'common' })

  const title = props.title ?? t('myplace')
  const description = props.description ?? DEFAULT_PAGE_DESCRIPTION
  const author = props.author ?? DEFAULT_PAGE_TITLE
  const { url: imageUrl, height = 630, width = 1200 } = props.image ?? { url: DEFAULT_PAGE_IMAGE }
  const pageUrl = props.pageUrl ?? `${links.ORIGIN}${router.basePath}${router.asPath}`

  const videoType = props.videoType ?? ''
  const ogType = videoType === 'clip' ? 'video' : ''
  const videoId = props.videoId ?? ''
  const channelName = props.channelName ?? ''
  const duration = props.duration ?? '0'

  return (
    <Helmet>
      <meta name="viewport" content="width=device-width, initial-scale=1, shrink-to-fit=no, user-scalable=no" />

      {'<!-- Primary Meta Tags -->'}
      <title>{title}</title>
      <meta name="title" content={title} />
      <meta name="description" content={description} />

      {'<!-- Open Graph / Facebook -->'}
      <meta property="og:url" content={toHttps(pageUrl)} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={imageUrl} />
      <meta property="og:image:width" content={width.toString()} />
      <meta property="og:image:height" content={height.toString()} />
      <meta property="article:author" content={author} />
      <meta property="article:publisher" content="네이버 MY플레이스" />

      {'<!-- Twitter -->'}
      <meta property="twitter:card" content="summary_large_image" />
      <meta property="twitter:url" content={toHttps(pageUrl)} />
      <meta property="twitter:title" content={title} />
      <meta property="twitter:description" content={description} />
      <meta property="twitter:image" content={imageUrl} />

      {'<!-- for clip meta tag -->'}
      <meta property="og:type" content={ogType} />
      <meta property="og:video:duration" content={duration} />
      <meta property="nv:video:type" content={videoType} />
      <meta property="nv:media:id" content={videoId} />
      <meta property="nv:channel:name" content={channelName} />

      <meta name="format-detection" content="telephone=no" />
    </Helmet>
  )
}

export default MetaTags
